import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import { isFetchingMore } from 'common/graphql/networkStatus';
import useCompanyContext from 'common/hooks/useCompanyContext';

import { DisputableTransactionsDocument } from './graphql/disputableTransactions.gql';

export const useDisputableTransactions = (
  transactionIds?: string[],
  first = 20,
) => {
  const { companyProfileId } = useCompanyContext();

  const {
    data: disputableTransactionsData,
    error: disputableTransactionsError,
    fetchMore: fetchMoreDisputableTransactions,
    networkStatus: disputableTransactionsNetworkStatus,
    refetch: refetchDisputableTransactions,
  } = useQuery(DisputableTransactionsDocument, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !companyProfileId,
    variables: {
      after: null,
      companyProfileId: companyProfileId || '',
      filters: {
        companyProfileId: companyProfileId || '',
        transactionIds,
      },
      first,
    },
  });

  const pageInfo =
    disputableTransactionsData?.viewer?.company?.disputableTransactions
      ?.pageInfo;

  const disputableTransactions = useMemo(
    () =>
      disputableTransactionsData?.viewer?.company?.disputableTransactions?.edges?.map(
        (edge) => edge.node,
      ),
    [disputableTransactionsData],
  );

  return {
    disputableTransactions: disputableTransactions ?? [],
    error: disputableTransactionsError,
    hasNextPage: !!pageInfo?.hasNextPage,
    isLoading: disputableTransactionsNetworkStatus === 1,
    isLoadingMore: isFetchingMore(disputableTransactionsNetworkStatus),
    loadMore: () =>
      fetchMoreDisputableTransactions({
        variables: {
          after: pageInfo?.nextCursor,
        },
      }),
    nextCursor: pageInfo?.nextCursor,
    refetch: refetchDisputableTransactions,
  };
};
